import type { AppError, ErrorSubtype } from '../../types/errors';

// Custom user-friendly messages for each error subtype
const ERROR_MESSAGES: Record<ErrorSubtype, string> = {
  // Validation Errors (400)
  invalid_email: 'Please enter a valid email address.',
  invalid_referral_code: 'Invalid referral code. Please check it and try again.',
  missing_password: 'Password is required.',
  validation_missing: 'Please fill in all required fields.',
  payment_exceeding: 'Payment amount exceeds your limit.',
  invalid_file_type: 'This file type is not supported.',
  file_too_large: 'File size is too large. Please choose a smaller file.',
  invalid_date_format: 'Please enter a valid date.',

  // Authentication Errors (401)
  no_api_key: 'API key is missing.',
  invalid_token: 'Your session has expired. Please log in again.',
  token_expired: 'Your session has expired. Please log in again.',
  wrong_credentials: 'Invalid email or password.',
  account_locked: 'Account is temporarily locked.',
  exceed_attempt_limits: 'Too many attempts. Please try again after 15 minutes.',

  // Authorization Errors (403)
  insufficient_permissions: 'You don\'t have permission to access this feature.',
  access_denied: 'Access denied. Please contact support.',
  account_suspended: 'Your account has been suspended.',
  feature_restricted: 'This feature is not available for your plan.',

  // Resource Errors (404)
  user_not_found: 'Incorrect email',
  lead_not_found: 'Lead not found',
  agent_not_found: 'Agent not found',
  conversation_not_found: 'Conversation not found',
  ticket_not_found: 'Support ticket not found',

  // Conflict Errors (409)
  email_already_registered: 'This email is already registered. Please log in.',
  user_already_registered: 'This email is already registered. Please log in.',
  duplicate_lead: 'Lead already exists',
  resource_in_use: 'This resource is currently in use',
  concurrent_modification: 'This resource was modified by another user',

  // Business Errors (402)
  insufficient_credits: 'You don\'t have enough credits',
  subscription_required: 'A premium subscription is required',
  payment_failed: 'Payment failed. Please try again',
  quota_exceeded: 'You\'ve reached your usage limit',

  // Rate Limit Errors (429)
  too_many_requests: 'Too many requests. Please wait a moment',
  daily_quota_reached: 'Daily limit reached. Please try again tomorrow',
  connection_limit: 'Too many active connections',

  // External Service Errors (424)
  stripe_service_down: 'Payment service is temporarily unavailable',
  email_service_failed: 'Email delivery failed. Please try again',
  s3_upload_failed: 'File upload failed. Please try again',
  webhook_delivery_failed: 'Webhook delivery failed',

  // System Errors (500+)
  database_connection_failed: 'Database connection failed',
  cache_service_down: 'Cache service is unavailable',
  internal_processing_error: 'An unexpected error occurred. Please try again',
  service_unavailable: 'Service is temporarily unavailable',
};

// Map AppError to user-friendly message
export function mapAppErrorMessage(error: AppError): string {
  return ERROR_MESSAGES[error.subtype] || error.message;
}

// Legacy function for backward compatibility - maps technical error messages to user-friendly ones
export function mapErrorMessage(errorMessage: string): string {
  const lowerError = errorMessage.toLowerCase();
  
  // Authentication errors
  if (lowerError.includes('invalid credentials') || 
      lowerError.includes('invalid email or password') ||
      lowerError.includes('wrong password') ||
      lowerError.includes('incorrect password') ||
      lowerError.includes('authentication failed') ||
      lowerError.includes('login failed')) {
    return 'Invalid email or password';
  }
  
  if (lowerError.includes('user not found') ||
      lowerError.includes('account not found') ||
      lowerError.includes('email not found')) {
    return 'Account not found';
  }
  
  if (lowerError.includes('user already exists') ||
      lowerError.includes('email already exists') ||
      lowerError.includes('account already exists') ||
      lowerError.includes('email is already registered')) {
    return 'User already exists';
  }
  
  if (lowerError.includes('email not verified') ||
      lowerError.includes('account not verified') ||
      lowerError.includes('verification required')) {
    return 'Please verify your email first';
  }
  
  if (lowerError.includes('account disabled') ||
      lowerError.includes('account suspended') ||
      lowerError.includes('account locked')) {
    return 'Account temporarily unavailable';
  }
  
  // Network errors
  if (lowerError.includes('network error') ||
      lowerError.includes('connection failed') ||
      lowerError.includes('timeout')) {
    return 'Connection problem. Please try again';
  }
  
  // Server errors
  if (lowerError.includes('server error') ||
      lowerError.includes('internal error') ||
      lowerError.includes('500') ||
      lowerError.includes('service unavailable')) {
    return 'Service temporarily unavailable';
  }
  
  // Validation errors
  if (lowerError.includes('invalid email format') ||
      lowerError.includes('email format')) {
    return ERROR_MESSAGES.invalid_email;
  }
  
  if (lowerError.includes('password too weak') ||
      lowerError.includes('password requirements')) {
    return 'Password does not meet requirements';
  }
  
  // Rate limiting
  if (lowerError.includes('too many requests') ||
      lowerError.includes('rate limit') ||
      lowerError.includes('try again later')) {
    return ERROR_MESSAGES.too_many_requests;
  }
  
  // Default fallback - remove technical jargon
  if (lowerError.includes('user credentials')) {
    return 'Login failed. Please try again';
  }
  
  // If it's already a simple message, return as is
  if (errorMessage.length < 50 && !lowerError.includes('error') && !lowerError.includes('failed')) {
    return errorMessage;
  }
  
  // Generic fallback
  return ERROR_MESSAGES.internal_processing_error;
}

// Additional helper for removing duplicate error displays
export function shouldDisplayError(fieldError: string, rootError: string | undefined): boolean {
  if (!rootError) return true;
  
  const normalizeMessage = (msg: string) => msg.toLowerCase().replace(/[^a-z]/g, '');
  
  return normalizeMessage(fieldError) !== normalizeMessage(rootError);
}